/**
 * MarkupUtil - A collection of HTML helpers and XSS helpers.
 */

// IMarkupFormatOptions - Parameter definition used by formatData
// breakOnFirstLine - When true, splits the string at the first instance of a line return or break and returns the zero index (left side).
// formatWithSpaces - When true, replaces line returns or breaks with a space. Option is redundant when breakOnFirstLine is true.
// plainTextContent - When true, will remove out markup tags.
interface IMarkupFormatOptions {
    breakOnFirstLine?: boolean;
    formatWithSpaces?: boolean;
    plainTextContent?: boolean;
}

// IReplaceMarkupTagsOptions
// replaceComments - When true, will replace HTML <!-- --> comment tags, when false, will ignore comment tags.
interface IReplaceMarkupTagsOptions {
    replaceComments?: boolean;
}

/**
 * formatText -  Helps format markup text using the IMarkupFormatOptions interface.
 *
 * @param value The value to format.
 * @param options The configuration options for the function based on IMarkupFormatOptions.
 * @return Returns the formatted value.
 */
function formatText(value: string, options: IMarkupFormatOptions): string {
    let ret = "";

    if (value === "") {
        return ret;
    }

    if (!value || typeof value !== "string") {
        throw new Error("core-markup-util.formatText: value missing required information.");
    }

    if (!options || typeof options !== "object") {
        throw new Error("core-markup-util.formatText: options missing required information.");
    }

    // Set default behavior if no options
    if (options.breakOnFirstLine == null) {
        options["breakOnFirstLine"] = true;
    }
    if (options.formatWithSpaces == null) {
        options["formatWithSpaces"] = true;
    }
    if (options.plainTextContent == null) {
        options["plainTextContent"] = true;
    }

    // Truncate the string at the first instance of a line break
    if (options.breakOnFirstLine === true) {
        if (value.indexOf("\\") > 0) {
            // Remove Microsoft carriage returns
            value = value.split("\\r\\n")[0];

            // Remove Apple / Unix carriage returns
            value = value.split("\\r")[0];

            // Remove Linux carriage returns
            value = value.split("\\n")[0];
        }

        if (value.indexOf("/") > 0) {
            // Remove HTML line breaks
            value = value.split("<br />")[0];
            value = value.split("<BR />")[0];
            value = value.split("<br/>")[0];
            value = value.split("<BR/>")[0];

            value = value.split("</p>")[0];
            value = value.split("</P>")[0];
            value = value.replace(/<p>/ig, "");
        }
        ret = value;
    }
    
    // Converts the line breaks into spaces
    // Does not need to run if breakOnFirstLine is true
    let charSpace = " ";
    if (options.formatWithSpaces === true && !options.breakOnFirstLine) {
        if (value.indexOf("\\") > 0) {
            // Replace Microsoft carriage returns with space
            value = value.replace(/\\r\\n/g, charSpace);

            // Replace Apple / Unix carriage returns with space
            value = value.replace(/\\r/g, charSpace);

            // Replace Linux carriage returns with space
            value = value.replace(/\\n/g, charSpace);
        }

        if (value.indexOf("/") > 0) {
            // Replace HTML line breaks with space
            value = value.replace(/<br[ ]?[\/]?>/ig, charSpace);

            value = value.replace(/<p>/ig, "");
            value = value.replace(/<\/p>/ig, charSpace);
        }
        ret = value;
    }

    // Make rich text plain text by removing html tags
    if (options.plainTextContent === true && typeof value === "string") {
        ret = replaceMarkupStringTags(value);
    }
    else {
        ret = value;
    }

    return ret;
}

/**
 * htmlTextToHtml -  Parses HTML text encoding into Html markup, e.g. "&lt;tag&gt;" becomes "<tag>".
 * 
 * Can be used dangerously and cause XSS, use only with content from static resources.
 *
 * @param value The Can be used dangerously and cause XSS, use only with content from static resources to format.
 * @return Returns the parsed value.
 */
function htmlTextToHtml(value: string): string {
    try {
        var parsedHtml = new DOMParser().parseFromString(value, "text/html");
    } catch (err) {
        // Polyfill for browsers which don't support DOMParser text/html
        var parsedHtml = document.implementation.createHTMLDocument("");
        parsedHtml.documentElement.innerHTML = value;
    }
    return parsedHtml.documentElement.textContent;
}

/**
 * htmlToHtmlText -  Converts HTML content into it"s text encoding equivelant, e.g. "<tag>" becomes "&lt;tag&gt;".
 * 
 * Can aid in XSS prevention as a safe encoding will not be interpreted as HTML by the browser.
 *
 * @param value The value to format.
 * @return Returns the converted value.
 */
function htmlToHtmlText(value: string): string {
    let htmlText = document.createTextNode(value);
    let p = document.createElement("p");
    p.appendChild(htmlText);
    return p.innerHTML;
}

type StringOrArray = string | StringOrArray[];
type NestedStringArray = StringOrArray;

/**
 * replaceMarkupTags -  Removes all <tags>, and </tags> from string or nested array
 * 
 * Helps with XSS prevention as no HTML element is present, just text content.
 * @param value The value to format.
 * @param options The configuration options for the function based on IMarkupFormatOptions.
 * @return Returns the formatted value.
 */
function replaceMarkupTags (value: string | NestedStringArray, options?: IReplaceMarkupTagsOptions): string | NestedStringArray {
    if (typeof value === 'string') return replaceMarkupStringTags(value, options);

    if (Array.isArray(value)) {
        value.forEach(function(val, index) {
            this[index] = replaceMarkupTags(val, options);
        }, value);

        return value;
    }
    else {
        // should not happen. But to error proof. There's possibility that currently it's send as numeric or any other type to the function.
        return replaceMarkupStringTags(value as any, options);
    }
}

/**
 * replaceMarkupStringTags -  Removes all <tags>, and </tags> from a string.
 * 
 * Helps with XSS prevention as no HTML element is present, just text content.
 *
 * @param value The value to format.
 * @param options The configuration options for the function based on IMarkupFormatOptions.
 * @return Returns the formatted value.
 */
function replaceMarkupStringTags (value: string, options?: IReplaceMarkupTagsOptions): string {
    if (!value) {
        return "";
    }

    if (typeof value === "object") {
        // if the value is an object, current function change the object to [object] instead of preserving original values
        throw new Error("markup util: value cannot be an object.");
    }

    if (!options) {
        options = options || {};
    }
    
    if (typeof options !== "object") {
        throw new Error("markup util: options missing required information.");
    }

    // Set default behavior if no options
    if (options.replaceComments == null) {
        options["replaceComments"] = false;
    }

    // Logic to preserve invalid HTML Elements like <Not Applicable>, <>, <NA>, etc
    value = value.toString().replace(/&lt;/g, "<").replace(/&gt;/g, ">"); // revert html character entity name to actual character if it exists
    const potentialMarkups = value.match(/<[^>]*>/g);

    if (potentialMarkups) {
        potentialMarkups.forEach(markup => {
            const tagFull = markup.toString().toLowerCase();

            // Comments are permitted, check for them first and preserve them
            const commentTags = tagFull.match(/<!--.*?-->/g);
            const commentTagStart = "<!--";
            const commentTagEnd = "-->";

            if (commentTags && commentTags.length > 0) {
                const commentTagStartLength = commentTagStart.length;
                const commentTagWithoutEndLength = tagFull.length - commentTagEnd.length;

                if (options["replaceComments"] === false) {
                    // Encode the value so it is preserved in the return statement
                    value = htmlToHtmlText(
                        commentTagStart
                        + markup.toString().substring(commentTagStartLength, commentTagWithoutEndLength)
                        + commentTagEnd
                    );
                }
                else {
                    value = value.toString().replace(/<[^>]*>/g, "");
                }
            }
            else {
                value = prepareMarkupByKnownElements(markup, value);
            }
        });
    }
    else {
        value = prepareMarkupByKnownElements(value, value);
    }

    // Logic to remove all valid HTML Tags
    return htmlTextToHtml(value);
}

/**
 * function for use within replaceMarkupStringTags only - to prepare markup with encoded characters for unknown HTML elements
 * 
 * @param markup section of the string that is a potential HTML element
 * @param value full string that contains the markup
 * @returns updated value with markup replaced with encoded characters
 */
function prepareMarkupByKnownElements(markup, value) {
    /**
     * List of known HTML elements. It is completely okay if we miss a few or if HTML introduce new elements in future
     * This is used to skip an expensive operation of detecting if an element is valid HTMLElement or not
     */
    const knownHtmlElements = {
        "a":1,"abbr":1,"address":1,"area":1,"article":1,"aside":1,"audio":1,"b":1,"base":1,"bdi":1,"bdo":1,"blockquote":1,
        "body":1,"br":1,"button":1,"canvas":1,"caption":1,"cite":1,"code":1,"col":1,"colgroup":1,"data":1,"datalist":1,
        "dd":1,"del":1,"details":1,"dfn":1,"dialog":1,"div":1,"dl":1,"dt":1,"em":1,"embed":1,"fieldset":1,"figcaption":1,
        "figure":1,"footer":1,"form":1,"h1":1,"h2":1,"h3":1,"h4":1,"h5":1,"h6":1,"head":1,"header":1,"hr":1,"html":1,
        "i":1,"iframe":1,"img":1,"input":1,"ins":1,"kbd":1,"label":1,"legend":1,"li":1,"link":1,"main":1,"map":1,"mark":1,
        "meta":1,"meter":1,"nav":1,"noscript":1,"object":1,"ol":1,"optgroup":1,"option":1,"output":1,"p":1,"param":1,
        "pre":1,"progress":1,"q":1,"rp":1,"rt":1,"ruby":1,"s":1,"samp":1,"script":1,"section":1,"select":1,"small":1,
        "source":1,"span":1,"strong":1,"style":1,"sub":1,"summary":1,"sup":1,"table":1,"tbody":1,"td":1,"textarea":1,
        "tfoot":1,"th":1,"thead":1,"time":1,"title":1,"tr":1,"track":1,"u":1,"ul":1,"var":1,"video":1,"wbr":1
    };
    /**
     * Few old browsers always return [object HTMLElement] for any tagName: Safari 5 is an example
     * document.createElement(tagName).toString() is always [object HTMLElement]
     * Meaning our logic will not retain it in response.
     * Following object can be used to add those tags which we want to retain no matter the case.
     * For example - <not applicable>
     * Please always add the first word before space
     */
    const tagsToRetain = {
        "not": 1,
    };

    let defaultEncode = false;

    const tagFull = markup.toString().toLowerCase();
    // document.createElement can be expensive operation.
    // Thats why we are checking tagName against pool of known html elements first
    let tagName = tagFull.replace("<", "").replace(">", "").replace("/", " ").trim().split(" ")[0];

    if (tagName === "" || tagsToRetain[tagName])
    {
        defaultEncode = true;
    }
    else if (!knownHtmlElements[tagName]) {
        try {
            const createdElement = document.createElement(tagName);
            if (createdElement.toString() === "[object HTMLUnknownElement]") {
                defaultEncode = true;
            }
        }
        catch (ex) {
            // We need to rely on the exception handler, we have so many unknown use cases that keep breaking the monolith code
            // This solution although not efficient, will handle the as of yet unknown edge cases.
            // If this code can be refactored in the future, to avoid using document.createElement.
            // We always need 100% coverage. Security functions cannot fail with exceptions based on input.
            defaultEncode = true;
        }
    }

    if (defaultEncode) {
        // If not a valid HTML Element, do not remove it and treat it as plain text
        value = value.replace(markup, markup.replace(/</g, "&lt;").replace(/>/g, "&gt;"));
    }

    return value;
}

export const MarkupUtil = {
    formatText, htmlTextToHtml, htmlToHtmlText, replaceMarkupTags
}

export default MarkupUtil;